import { changePasswordInput, profileInput } from "@bt/domain";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, BanIcon, ImageUpIcon, Trash2Icon, UserXIcon } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Avatar } from "@/components/app/avatar";
import { Field } from "@/components/app/field";
import { Logo } from "@/components/app/logo";
import { useSignOut } from "@/components/app/user-menu";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { type Me, signInUrl, useMe } from "@/lib/account";
import { errorMessage } from "@/lib/errors";
import { useFormat } from "@/lib/format";
import { navigate } from "@/lib/pages";
import { useTRPC } from "@/lib/trpc";
import { uploadAvatar } from "@/lib/upload";
import { AuthCard } from "./account-app";
import { FormError, fieldErrors, NewPasswordFields, newPasswordErrors } from "./sign-in";

/** /account/profile: name, picture, password, and deleting the account. */
export function ProfilePage() {
  const { t } = useTranslation();
  const { data: me, isPending } = useMe();

  useEffect(() => {
    if (isPending) return;
    if (!me) window.location.replace(signInUrl());
    else if (me.status !== "active") window.location.replace("/account/restricted");
  }, [me, isPending]);
  if (me?.status !== "active") return null;

  return (
    <div className="min-h-dvh bg-background">
      <header className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-2 sm:px-6">
        <a
          href="/"
          className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <Logo />
        </a>
        <Button asChild variant="ghost" size="sm">
          <a href="/">
            <ArrowLeftIcon />
            {t("account.backToGarages")}
          </a>
        </Button>
      </header>
      <main className="mx-auto flex max-w-2xl flex-col gap-6 px-4 pt-4 pb-16 sm:px-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("account.profileTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("account.profileBody")}</p>
        </div>
        <ProfileCard me={me} />
        <PasswordCard />
        <DeleteCard />
      </main>
    </div>
  );
}

function Section({ title, body, children }: { title: string; body?: string; children: ReactNode }) {
  return (
    <Card className="p-4 sm:p-5">
      <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
      {body ? <p className="mt-0.5 text-sm text-muted-foreground">{body}</p> : null}
      <div className="mt-4">{children}</div>
    </Card>
  );
}

function ProfileCard({ me }: { me: Me }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const queryClient = useQueryClient();
  const update = useMutation({
    ...trpc.account.updateProfile.mutationOptions(),
    meta: { silent: true },
  });
  const removeAvatar = useMutation(trpc.account.removeAvatar.mutationOptions());
  const fileInput = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState(me.firstName ?? "");
  const [lastName, setLastName] = useState(me.lastName ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);

  return (
    <Section title={t("account.nameAndPicture")}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <Avatar user={me} className="size-16 text-lg" />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
            >
              <ImageUpIcon />
              {t("account.uploadPicture")}
            </Button>
            {me.avatarUrl ? (
              <Button variant="ghost" size="sm" onClick={() => removeAvatar.mutate()}>
                <Trash2Icon />
                {t("account.removePicture")}
              </Button>
            ) : null}
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="avatar-file"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                setUploading(true);
                try {
                  await uploadAvatar(file);
                  await queryClient.invalidateQueries();
                  toast.success(t("account.pictureSaved"));
                } catch (error) {
                  toast.error(errorMessage(error, t));
                } finally {
                  setUploading(false);
                }
              }}
            />
          </div>
        </div>
        <form
          className="flex flex-col gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            const parsed = profileInput.safeParse({ firstName, lastName });
            if (!parsed.success) {
              setErrors(fieldErrors(parsed.error.issues));
              return;
            }
            setErrors({});
            await update.mutateAsync(parsed.data);
            toast.success(t("account.profileSaved"));
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("account.firstName")} htmlFor="profile-first" error={errors.firstName}>
              <Input
                id="profile-first"
                autoComplete="given-name"
                value={firstName}
                aria-invalid={errors.firstName ? true : undefined}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field label={t("account.lastName")} htmlFor="profile-last" error={errors.lastName}>
              <Input
                id="profile-last"
                autoComplete="family-name"
                value={lastName}
                aria-invalid={errors.lastName ? true : undefined}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
          </div>
          <Field label={t("account.email")} htmlFor="profile-email" hint={t("account.emailHint")}>
            <Input id="profile-email" value={me.email ?? ""} readOnly />
          </Field>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">{t("account.createdOn")}</dt>
              <dd>{format.date(me.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{t("account.lastSignIn")}</dt>
              <dd>{format.dateTime(me.lastLoginAt)}</dd>
            </div>
          </dl>
          <Button type="submit" className="self-start" disabled={update.isPending}>
            {t("common.saveChanges")}
          </Button>
        </form>
      </div>
    </Section>
  );
}

function PasswordCard() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const change = useMutation({
    ...trpc.account.changePassword.mutationOptions(),
    meta: { silent: true },
  });
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  return (
    <Section title={t("account.changePassword")} body={t("account.changePasswordBody")}>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (event) => {
          event.preventDefault();
          const problems = { ...(newPasswordErrors(password, repeat) ?? {}) } as Record<
            string,
            string | undefined
          >;
          if (!changePasswordInput.shape.current.safeParse(current).success) {
            problems.current = "validation.required";
          }
          setErrors(problems);
          if (Object.values(problems).some(Boolean)) return;
          setServerError(null);
          try {
            await change.mutateAsync({ current, next: password });
            setCurrent("");
            setPassword("");
            setRepeat("");
            toast.success(t("account.passwordChanged"));
          } catch (error) {
            setServerError(errorMessage(error, t));
          }
        }}
      >
        <Field
          label={t("account.currentPassword")}
          htmlFor="password-current"
          error={errors.current}
        >
          <Input
            id="password-current"
            type="password"
            autoComplete="current-password"
            value={current}
            aria-invalid={errors.current ? true : undefined}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <NewPasswordFields
          idPrefix="password-new"
          password={password}
          repeat={repeat}
          onPassword={setPassword}
          onRepeat={setRepeat}
          errors={errors}
        />
        <FormError message={serverError} />
        <Button type="submit" className="self-start" disabled={change.isPending}>
          {t("account.changePassword")}
        </Button>
      </form>
    </Section>
  );
}

function DeleteCard() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Section title={t("account.deleteTitle")} body={t("account.deleteBody")}>
      <Button variant="destructive" onClick={() => setOpen(true)}>
        <UserXIcon />
        {t("account.deleteConfirm")}
      </Button>
      <DeleteAccountDialog open={open} onOpenChange={setOpen} />
    </Section>
  );
}

/** "Delete my account", confirmed with the password; from the profile or the restricted page. */
export function DeleteAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const remove = useMutation({
    ...trpc.account.deleteMyAccount.mutationOptions(),
    meta: { silent: true },
  });
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title={t("account.deleteConfirmTitle")}
          description={t("account.deleteBody")}
        />
        <form
          className="contents"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!password) {
              setError(t("validation.required"));
              return;
            }
            try {
              await remove.mutateAsync({ password });
              // Switch pages here: a full load would race this page's "signed out" redirect.
              queryClient.setQueryData(trpc.account.me.queryKey(), null);
              navigate("/account/sign-in?deleted=1", { replace: true });
            } catch (e) {
              setError(errorMessage(e, t));
            }
          }}
        >
          <DialogBody className="flex flex-col gap-4">
            <Field label={t("account.deletePassword")} htmlFor="delete-password">
              <Input
                id="delete-password"
                type="password"
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <FormError message={error} />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" disabled={remove.isPending}>
              {t("account.deleteConfirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** /account/restricted: a disabled or deleted account can only sign out or be deleted. */
export function RestrictedPage() {
  const { t } = useTranslation();
  const { data: me, isPending } = useMe();
  const signOut = useSignOut();
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isPending) return;
    if (!me) window.location.replace("/account/sign-in");
    else if (me.status === "active") window.location.replace("/");
  }, [me, isPending]);
  if (!me || me.status === "active") return null;

  const disabled = me.status === "disabled";
  return (
    <AuthCard
      title={disabled ? t("account.disabledTitle") : t("account.deletedTitle")}
      body={disabled ? t("account.disabledBody") : t("account.deletedBody")}
    >
      <div className="flex flex-col gap-3">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <BanIcon className="size-4 shrink-0" />
          {me.email}
        </p>
        <Button variant="outline" onClick={() => void signOut()}>
          {t("account.signOut")}
        </Button>
        <Button variant="destructive" onClick={() => setDeleting(true)}>
          <UserXIcon />
          {t("account.deleteConfirm")}
        </Button>
      </div>
      <DeleteAccountDialog open={deleting} onOpenChange={setDeleting} />
    </AuthCard>
  );
}
